import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/feature/Footer';
import NeighborhoodSelector from './components/NeighborhoodSelector';

export default function HomePage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    situation: '',
    timeline: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const scrollToForm = () => {
    const formSection = document.getElementById('contact-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePropertySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const data: Record<string, string> = {};
    formData.forEach((value, key) => {
      data[key] = value.toString();
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d64qqrir6bioa6qf6k3g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(data).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          address: '',
          situation: '',
          timeline: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const distressedHomes = [
    {
      title: "Fire Damaged Property",
      image: "https://public.readdy.ai/ai/img_res/3cbcad190067b81ab53774f53af3ee08.jpg"
    },
    {
      title: "Inherited Property",
      image: "https://readdy.ai/api/search-image?query=Old%20inherited%20house%20needing%20repairs%20overgrown%20yard%20neglected%20exterior%20peeling%20paint%20worn%20roof%20traditional%20El%20Paso%20Texas%20home%20vintage%20architecture%20estate%20property%20realistic%20photography%20natural%20lighting%20showing%20age%20and%20maintenance%20needs&width=400&height=300&seq=inherit1&orientation=landscape"
    },
    {
      title: "Foreclosure Property",
      image: "https://readdy.ai/api/search-image?query=Foreclosed%20house%20with%20bank%20notice%20sign%20vacant%20property%20distressed%20condition%20El%20Paso%20Texas%20residential%20home%20realistic%20photography%20showing%20abandonment%20neglect%20empty%20windows%20overgrown%20landscaping%20urgent%20foreclosure%20sale&width=400&height=300&seq=foreclosure1&orientation=landscape"
    },
    {
      title: "Fixer-Upper",
      image: "https://readdy.ai/api/search-image?query=Fixer%20upper%20house%20needing%20major%20repairs%20broken%20fence%20damaged%20siding%20cracked%20driveway%20El%20Paso%20Texas%20residential%20property%20realistic%20photography%20showing%20renovation%20needs%20handyman%20special%20investment%20opportunity%20distressed%20condition&width=400&height=300&seq=fixer1&orientation=landscape"
    },
    {
      title: "Divorce Sale",
      image: "https://readdy.ai/api/search-image?query=House%20for%20quick%20divorce%20sale%20standard%20residential%20property%20El%20Paso%20Texas%20neighborhood%20realistic%20photography%20clean%20exterior%20normal%20condition%20family%20home%20needing%20fast%20sale%20urgent%20transaction%20required&width=400&height=300&seq=divorce1&orientation=landscape"
    },
    {
      title: "Behind on Payments",
      image: "https://readdy.ai/api/search-image?query=House%20with%20financial%20distress%20behind%20on%20mortgage%20payments%20worried%20homeowner%20situation%20El%20Paso%20Texas%20residential%20property%20realistic%20photography%20showing%20normal%20exterior%20but%20urgent%20sale%20needed%20to%20avoid%20foreclosure&width=400&height=300&seq=payments1&orientation=landscape"
    }
  ];

  const blogPosts = [
    {
      title: "How to Sell Your House Fast in El Paso",
      excerpt: "Discover the fastest way to sell your El Paso property without repairs, showings, or agent fees.",
      link: "/get-cash/sell-house-fast-el-paso",
      image: "https://readdy.ai/api/search-image?query=El%20Paso%20Texas%20residential%20neighborhood%20houses%20street%20view%20sunny%20day%20realistic%20photography%20showing%20typical%20homes%20southwestern%20architecture%20desert%20landscape%20mountains%20in%20background&width=400&height=250&seq=blog1&orientation=landscape"
    },
    {
      title: "What to Do When You Inherit a House in Texas",
      excerpt: "Learn your options when inheriting property in Texas and how to sell quickly without the hassle.",
      link: "/get-cash/inherit-house-texas",
      image: "https://readdy.ai/api/search-image?query=Inherited%20old%20house%20Texas%20property%20vintage%20architecture%20estate%20home%20realistic%20photography%20showing%20aged%20exterior%20traditional%20design%20family%20home%20needing%20decisions%20about%20sale%20or%20keeping&width=400&height=250&seq=blog2&orientation=landscape"
    },
    {
      title: "Avoiding Foreclosure: Your Options Explained",
      excerpt: "Facing foreclosure? Explore alternatives that can help you avoid losing your home to the bank.",
      link: "/get-cash/avoiding-foreclosure-options",
      image: "https://readdy.ai/api/search-image?query=Worried%20homeowner%20looking%20at%20house%20financial%20stress%20foreclosure%20concern%20realistic%20photography%20showing%20residential%20property%20with%20concerned%20perspective%20financial%20difficulty%20situation&width=400&height=250&seq=blog3&orientation=landscape"
    }
  ];

  const faqs = [
    {
      question: "How quickly can you buy my house?",
      answer: "We can close on your property in as little as 7 days. Unlike traditional sales that take 30-60 days or longer, our streamlined cash buying process eliminates financing contingencies, lengthy inspections, and buyer qualification delays. Once you accept our offer, we work with a local title company to handle all the paperwork efficiently. If you need more time to move, we can accommodate your schedule. We've helped hundreds of El Paso homeowners close quickly when they needed it most."
    },
    {
      question: "Do I need to make any repairs before selling?",
      answer: "Absolutely not. We buy houses in any condition throughout El Paso. Whether your property has fire damage, foundation issues, roof problems, plumbing leaks, outdated kitchens, or hasn't been maintained in years, we'll make you a fair cash offer. You don't need to paint, clean, or fix anything. We handle all repairs after closing, saving you thousands of dollars and weeks of hassle. This is perfect for inherited properties, rental properties with tenant damage, or homes that need extensive work."
    },
    {
      question: "How do you determine your offer price?",
      answer: "Our offer is based on several factors: your property's location in El Paso, the current condition, comparable sales in your neighborhood, and current market conditions. We consider repair costs, holding costs, and our business expenses. While we can't pay full retail market value like a traditional buyer might, we offer the convenience of a fast cash sale with no repairs, no showings, no agent commissions, and a guaranteed close. Many sellers find our net proceeds comparable to listing with an agent after deducting all their costs and hassles."
    },
    {
      question: "Are there any fees or commissions?",
      answer: "No hidden fees whatsoever. Unlike selling with a real estate agent where you'd pay 6% commission plus closing costs, repairs, and holding costs, we cover all closing costs. There are no agent commissions, no inspection fees, no appraisal costs, and no surprise deductions at closing. The offer we present is the amount you'll receive at closing (minus any liens or mortgages on the property). We believe in complete transparency throughout the entire process."
    },
    {
      question: "What types of properties do you buy?",
      answer: "We buy all types of residential properties in El Paso and surrounding areas: single-family homes, townhouses, condos, multi-family properties, mobile homes, and vacant land. We purchase properties in any condition - from pristine move-in ready homes to complete tear-downs. We buy inherited properties, foreclosures, properties with code violations, fire-damaged homes, properties with foundation issues, homes behind on taxes, and rental properties with problem tenants. If you own it in El Paso, we're interested in making an offer."
    },
    {
      question: "What areas of El Paso do you serve?",
      answer: "We buy houses throughout all of El Paso County and surrounding areas. This includes Central El Paso, East El Paso, West El Paso, Northeast El Paso, Mission Valley, Eastside, Westside, Lower Valley, Upper Valley, Cielo Vista, Kern Place, Sunset Heights, Manhattan Heights, and all surrounding communities. We also purchase properties in nearby areas like Socorro, Horizon City, Clint, Fabens, Anthony, and Canutillo. No matter where your property is located in the El Paso region, we'd love to make you a fair cash offer."
    },
    {
      question: "Can you buy my house if I'm behind on payments?",
      answer: "Yes, we specialize in helping homeowners who are behind on mortgage payments avoid foreclosure. We can work directly with your lender to pay off your mortgage and any back payments, stopping the foreclosure process. Time is critical in these situations, which is why our quick closing process is so valuable. We've helped many El Paso families escape the stress of foreclosure and move forward with their lives. Contact us immediately if you're facing foreclosure - the sooner we talk, the more options you'll have."
    },
    {
      question: "What if my house has tenants?",
      answer: "Not a problem at all. We regularly purchase rental properties with tenants in place. Whether you have good tenants on a lease, problem tenants you want to avoid evicting, or tenants who aren't paying rent, we can handle the situation. We can purchase the property with the tenants and take over the landlord responsibilities, or we can work with you to transition the tenants out before or after closing. Many landlords sell to us when they're tired of property management hassles or dealing with difficult tenant situations."
    },
    {
      question: "How is selling to you different from listing with an agent?",
      answer: "The differences are significant. With an agent, you'll wait months for a buyer, make repairs and updates, keep your house show-ready for constant showings, pay 6% commission plus closing costs, risk deals falling through due to financing issues, and deal with inspection negotiations. When you sell to us, you get a cash offer within 24 hours, close in as little as 7 days, make zero repairs, have no showings or open houses, pay no commissions or fees, and have a guaranteed close with no financing contingencies. You choose the closing date that works for your schedule."
    },
    {
      question: "Is selling to a cash buyer legitimate?",
      answer: "Absolutely. Cash home buying is a legitimate, established industry that's been helping homeowners for decades. We're a professional real estate investment company operating legally in El Paso. All transactions are handled through licensed title companies with proper legal documentation. You'll have time to review all paperwork with an attorney if you choose. We're members of local business organizations and have helped hundreds of El Paso families sell their properties quickly and fairly. We encourage you to research us, read reviews, and ask questions. Our reputation in the El Paso community is built on honest, transparent transactions."
    }
  ];

  useEffect(() => {
    // Update page title and meta description dynamically
    document.title = 'Sell House Fast El Paso TX for Cash | We Buy Houses 915-745-7883';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Need to sell your house fast in El Paso? We buy houses for cash in any condition. No repairs, no fees, no commissions. Close in 7 days. Free cash offer in 24 hours. Serving all El Paso neighborhoods including East El Paso, West El Paso, Northeast, Lower Valley, and all surrounding areas. Call 915-745-7883.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <header className="bg-black text-white py-6 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center">
                <i className="ri-home-4-fill text-2xl text-black"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold">Cash For Houses El Paso</h1>
                <p className="text-sm text-gray-300">Sell Your House Fast in El Paso, Texas</p>
              </div>
            </div>
            <a href="tel:9157457883" className="bg-yellow-500 text-black px-6 py-3 rounded-lg font-bold hover:bg-yellow-400 transition-colors flex items-center gap-2" aria-label="Call us at 915-745-7883">
              <i className="ri-phone-fill"></i>
              915-745-7883
            </a>
          </div>
        </div>
      </header>

      {/* Hero Content */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Text Content */}
            <div className="text-center lg:text-left">
              <h2 className="text-5xl font-bold mb-6">Sell Your <strong>El Paso House Fast for Cash</strong></h2>
              <p className="text-xl mb-8 text-gray-300"><strong>No Repairs</strong> • <strong>No Fees</strong> • <strong>No Hassles</strong> • <strong>Close in 7 Days</strong></p>
              <p className="text-lg mb-8 text-gray-300">We buy houses in <strong>any condition</strong> throughout <strong>El Paso, Texas</strong> including East El Paso, West El Paso, Northeast, Lower Valley, and all surrounding areas.</p>
              <a href="tel:9157457883" className="inline-block bg-yellow-500 text-black px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors" aria-label="Call now to sell your house fast">
                Call Now: 915-745-7883
              </a>
            </div>

            {/* Right Side - Contact Form */}
            <div id="contact-form" className="bg-white rounded-3xl p-8 shadow-2xl">
              <h3 className="text-2xl font-bold text-black mb-2 text-center">Get Your Cash Offer Today</h3>
              <p className="text-gray-600 mb-6 text-center text-sm">Fill out the form and we'll contact you within 24 hours</p>
              
              <form 
                data-readdy-form
                id="property-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  
                  const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement;
                  const statusDiv = document.getElementById('form-status');
                  
                  if (submitButton) submitButton.disabled = true;
                  if (statusDiv) statusDiv.textContent = 'Submitting...';

                  try {
                    const response = await fetch('https://readdy.ai/api/form/d64p784bd5onm6bapmig', {
                      method: 'POST',
                      body: new URLSearchParams(formData as any),
                    });

                    if (response.ok) {
                      if (statusDiv) {
                        statusDiv.textContent = 'Thank you! We will contact you within 24 hours.';
                        statusDiv.className = 'mt-4 p-3 bg-green-100 text-green-800 rounded-lg text-center font-semibold text-sm';
                      }
                      form.reset();
                    } else {
                      if (statusDiv) {
                        statusDiv.textContent = 'Something went wrong. Please call us at 915-745-7883.';
                        statusDiv.className = 'mt-4 p-3 bg-red-100 text-red-800 rounded-lg text-center font-semibold text-sm';
                      }
                    }
                  } catch (error) {
                    if (statusDiv) {
                      statusDiv.textContent = 'Something went wrong. Please call us at 915-745-7883.';
                      statusDiv.className = 'mt-4 p-3 bg-red-100 text-red-800 rounded-lg text-center font-semibold text-sm';
                    }
                  } finally {
                    if (submitButton) submitButton.disabled = false;
                  }
                }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="name" className="block text-xs font-semibold text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm text-black"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs font-semibold text-gray-700 mb-1">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm text-black"
                      placeholder="915-123-4567"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="email" className="block text-xs font-semibold text-gray-700 mb-1">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm text-black"
                    placeholder="john@example.com"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="address" className="block text-xs font-semibold text-gray-700 mb-1">Property Address *</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    required
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm text-black"
                    placeholder="123 Main St, El Paso, TX"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="condition" className="block text-xs font-semibold text-gray-700 mb-1">Property Condition</label>
                    <select
                      id="condition"
                      name="condition"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm text-black"
                    >
                      <option value="Excellent">Excellent - Move-in Ready</option>
                      <option value="Good">Good - Minor Repairs</option>
                      <option value="Fair">Fair - Some Repairs</option>
                      <option value="Poor">Poor - Major Repairs</option>
                      <option value="Distressed">Distressed - Significant Damage</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="timeline" className="block text-xs font-semibold text-gray-700 mb-1">When to sell?</label>
                    <select
                      id="timeline"
                      name="timeline"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm text-black"
                    >
                      <option value="ASAP">As soon as possible</option>
                      <option value="1-2 weeks">Within 1-2 weeks</option>
                      <option value="1 month">Within 1 month</option>
                      <option value="2-3 months">Within 2-3 months</option>
                      <option value="Flexible">Flexible timeline</option>
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="message" className="block text-xs font-semibold text-gray-700 mb-1">Additional Information</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={2}
                    maxLength={500}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm resize-none text-black"
                    placeholder="Tell us more about your situation..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-black text-white text-base font-semibold rounded-full hover:scale-105 hover:shadow-2xl transition-all whitespace-nowrap cursor-pointer"
                >
                  Get My Cash Offer
                </button>

                <div id="form-status" className="mt-3"></div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-gray-50 py-8 px-4 border-b">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-yellow-600 mb-2">500+</div>
              <div className="text-sm text-gray-600">Houses Purchased</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-yellow-600 mb-2">7 Days</div>
              <div className="text-sm text-gray-600">Average Close Time</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-yellow-600 mb-2">$0</div>
              <div className="text-sm text-gray-600">Fees or Commissions</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-yellow-600 mb-2">100%</div>
              <div className="text-sm text-gray-600">Satisfaction Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="bg-white py-24">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-sm uppercase tracking-widest text-gray-500 mb-4">THE PROCESS</p>
          <h2 className="text-5xl font-bold text-black mb-16">Three Simple Steps to Sell Your House</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative bg-[#FAF9F6] rounded-3xl p-10 shadow-sm hover:shadow-lg hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="absolute -top-6 -left-3 w-16 h-16 bg-black rounded-full flex items-center justify-center">
                <span className="text-white text-3xl font-bold">1</span>
              </div>
              <div className="w-12 h-12 flex items-center justify-center mt-6 mb-6">
                <i className="ri-phone-line text-5xl text-black"></i>
              </div>
              <h3 className="text-2xl font-semibold text-black mb-4">Contact Us</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Call us at 915-206-5814 or fill out our simple form. Tell us about your property and situation. We'll schedule a convenient time to discuss your needs and answer any questions you have about selling your house fast in El Paso.
              </p>
            </div>

            {/* Step 2 */}
            <div className="relative bg-[#FAF9F6] rounded-3xl p-10 shadow-sm hover:shadow-lg hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="absolute -top-6 -left-3 w-16 h-16 bg-black rounded-full flex items-center justify-center">
                <span className="text-white text-3xl font-bold">2</span>
              </div>
              <div className="w-12 h-12 flex items-center justify-center mt-6 mb-6">
                <i className="ri-file-list-3-line text-5xl text-black"></i>
              </div>
              <h3 className="text-2xl font-semibold text-black mb-4">Get Your Offer</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                We'll evaluate your property and present you with a fair, no-obligation cash offer within 24 hours. Our offers are based on current market conditions and the as-is condition of your home. No waiting, no uncertainty - just a straightforward cash offer.
              </p>
            </div>

            {/* Step 3 */}
            <div className="relative bg-[#FAF9F6] rounded-3xl p-10 shadow-sm hover:shadow-lg hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <div className="absolute -top-6 -left-3 w-16 h-16 bg-black rounded-full flex items-center justify-center">
                <span className="text-white text-3xl font-bold">3</span>
              </div>
              <div className="w-12 h-12 flex items-center justify-center mt-6 mb-6">
                <i className="ri-money-dollar-circle-line text-5xl text-black"></i>
              </div>
              <h3 className="text-2xl font-semibold text-black mb-4">Close Fast</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Choose your closing date - we can close in as little as 7 days or on your timeline. We handle all the paperwork and closing costs. Walk away with cash in hand and move forward with your life without the stress of traditional home selling.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Distressed Homes Gallery */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-black mb-4">We Buy Houses in Any Condition</h2>
            <p className="text-xl text-gray-600">No repairs needed - we buy as-is</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to="/get-cash/fire-damaged-property-el-paso" className="relative rounded-2xl overflow-hidden group cursor-pointer h-72 block">
              <img 
                src="https://readdy.ai/api/search-image?query=Fire%20damaged%20house%20exterior%20with%20visible%20burn%20marks%20charred%20walls%20broken%20windows%20distressed%20residential%20property%20in%20El%20Paso%20Texas%20neighborhood%20realistic%20photography%20dramatic%20lighting%20showing%20structural%20damage%20smoke%20stains%20deteriorated%20condition%20urgent%20sale%20needed&width=400&height=300&seq=fire1&orientation=landscape" 
                alt="Fire Damaged Home" 
                className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-300"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white text-lg font-semibold">Fire Damaged Home</p>
              </div>
            </Link>

            <Link to="/get-cash/inherit-house-texas" className="relative rounded-2xl overflow-hidden group cursor-pointer h-72 block">
              <img 
                src="https://readdy.ai/api/search-image?query=Old%20inherited%20house%20needing%20repairs%20overgrown%20yard%20neglected%20exterior%20peeling%20paint%20worn%20roof%20traditional%20El%20Paso%20Texas%20home%20vintage%20architecture%20estate%20property%20realistic%20photography%20natural%20lighting%20showing%20age%20and%20maintenance%20needs&width=400&height=300&seq=inherit1&orientation=landscape" 
                alt="Inherited Property" 
                className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-300"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white text-lg font-semibold">Inherited Property</p>
              </div>
            </Link>

            <Link to="/get-cash/avoiding-foreclosure-options" className="relative rounded-2xl overflow-hidden group cursor-pointer h-72 block">
              <img 
                src="https://readdy.ai/api/search-image?query=Foreclosed%20house%20with%20bank%20notice%20sign%20vacant%20property%20distressed%20condition%20El%20Paso%20Texas%20residential%20home%20realistic%20photography%20showing%20abandonment%20neglect%20empty%20windows%20overgrown%20landscaping%20urgent%20foreclosure%20sale&width=400&height=300&seq=foreclosure1&orientation=landscape" 
                alt="Foreclosure" 
                className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-300"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white text-lg font-semibold">Foreclosure</p>
              </div>
            </Link>

            <Link to="/get-cash/sell-house-code-violations-el-paso" className="relative rounded-2xl overflow-hidden group cursor-pointer h-72 block">
              <img 
                src="https://readdy.ai/api/search-image?query=Fixer%20upper%20house%20needing%20major%20repairs%20broken%20fence%20damaged%20siding%20cracked%20driveway%20El%20Paso%20Texas%20residential%20property%20realistic%20photography%20showing%20renovation%20needs%20handyman%20special%20investment%20opportunity%20distressed%20condition&width=400&height=300&seq=fixer1&orientation=landscape" 
                alt="Fixer-Upper" 
                className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-300"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white text-lg font-semibold">Fixer-Upper</p>
              </div>
            </Link>

            <Link to="/get-cash/sell-house-divorce-el-paso" className="relative rounded-2xl overflow-hidden group cursor-pointer h-72 block">
              <img 
                src="https://readdy.ai/api/search-image?query=House%20for%20quick%20divorce%20sale%20standard%20residential%20property%20El%20Paso%20Texas%20neighborhood%20realistic%20photography%20clean%20exterior%20normal%20condition%20family%20home%20needing%20fast%20sale%20urgent%20transaction%20required&width=400&height=300&seq=divorce1&orientation=landscape" 
                alt="Divorce Sale" 
                className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-300"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white text-lg font-semibold">Divorce Sale</p>
              </div>
            </Link>

            <Link to="/get-cash/cant-pay-mortgage-el-paso" className="relative rounded-2xl overflow-hidden group cursor-pointer h-72 block">
              <img 
                src="https://readdy.ai/api/search-image?query=House%20with%20financial%20distress%20behind%20on%20mortgage%20payments%20worried%20homeowner%20situation%20El%20Paso%20Texas%20residential%20property%20realistic%20photography%20showing%20normal%20exterior%20but%20urgent%20sale%20needed%20to%20avoid%20foreclosure&width=400&height=300&seq=payments1&orientation=landscape" 
                alt="Behind on Payments" 
                className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-300"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white text-lg font-semibold">Behind on Payments</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Explainer Section */}
      <section className="bg-white py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="rounded-3xl overflow-hidden shadow-xl h-96">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20handshake%20between%20real%20estate%20investor%20and%20happy%20homeowner%20closing%20deal%2C%20business%20agreement%20moment%2C%20warm%20office%20setting%20with%20natural%20lighting%2C%20professional%20photography%20capturing%20trust%20and%20satisfaction&width=700&height=600&seq=handshake-001&orientation=portrait" 
                alt="Selling your house" 
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div>
              <p className="text-sm uppercase tracking-widest text-[#D4AF37] mb-4 font-semibold">WHY CHOOSE US</p>
              <h2 className="text-5xl font-bold text-black mb-8">We Don't Pay Market Value, But We Buy FAST and As-Is</h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-12">
                Let's be transparent: we're real estate investors, not retail buyers. Our offers reflect the as-is condition of your property and the speed at which we can close. While you might get a higher price listing with a realtor and waiting months, we offer immediate cash, zero repairs, no fees, and a closing date that works for you. For homeowners facing foreclosure, divorce, inheritance issues, or those who simply need to sell quickly, our service provides the fastest path to cash without the traditional hassles of selling a home.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-500 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <p className="text-xl font-semibold text-black">No Realtor Commissions</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-500 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <p className="text-xl font-semibold text-black">No Closing Costs</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-500 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <p className="text-xl font-semibold text-black">No Repairs Required</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-500 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <p className="text-xl font-semibold text-black">Close on Your Timeline</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-500 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <p className="text-xl font-semibold text-black">Cash Payment Guaranteed</p>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-500 rounded-full flex-shrink-0 mt-1">
                    <i className="ri-check-line text-white text-lg"></i>
                  </div>
                  <p className="text-xl font-semibold text-black">We Handle All Paperwork</p>
                </div>
              </div>

              <button onClick={scrollToForm} className="mt-12 px-10 py-4 bg-black text-white text-lg font-semibold rounded-full hover:bg-gray-800 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
                Get Started Today
                <i className="ri-arrow-right-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About El Paso Section */}
      <section id="about-elpaso" className="bg-[#FFFEF7] py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23000000\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-black mb-6">Serving All of El Paso, Texas</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-8"></div>
            <p className="text-xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
              El Paso is a vibrant border city nestled against the Franklin Mountains, offering a unique blend of cultures, history, and opportunity. As the sixth-largest city in Texas, El Paso County encompasses diverse neighborhoods from the historic downtown area to the growing eastside communities. We proudly serve homeowners throughout El Paso County, including all neighborhoods and surrounding areas. Whether your property is in the heart of downtown, the scenic westside near the mountains, the family-friendly northeast, or anywhere in the greater El Paso area, we're ready to make you a fair cash offer. Our deep knowledge of the local real estate market allows us to provide competitive offers that reflect your property's true value in the El Paso market.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mt-16">
            {['East El Paso', 'West El Paso', 'Central El Paso', 'Northeast El Paso', 'Mission Valley', 'Eastside', 'Westside', 'Lower Valley', 'Upper Valley', 'Downtown', 'Kern Place', 'Sunset Heights'].map((area) => (
              <span key={area} className="px-6 py-3 bg-white rounded-full text-gray-700 text-base font-medium shadow-sm hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Neighborhood Selector */}
      <NeighborhoodSelector />

      {/* Benefits Section */}
      <section id="benefits" className="bg-white py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-black mb-6">Why Sell to Cash Buyers in El Paso?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Traditional home sales can take months and cost thousands. We offer a better alternative for homeowners who need to sell quickly.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="w-14 h-14 flex items-center justify-center bg-black rounded-xl mb-6">
                <i className="ri-time-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Speed and Convenience</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Traditional home sales average 60-90 days from listing to closing. With us, you can close in as little as 7 days. No waiting for buyer financing to fall through, no endless showings disrupting your life, and no uncertainty about when you'll receive your money. We provide a definite closing date that works with your schedule, giving you the freedom to plan your next move with confidence.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="w-14 h-14 flex items-center justify-center bg-black rounded-xl mb-6">
                <i className="ri-tools-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Sell As-Is Condition</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Forget about spending thousands on repairs, renovations, or updates. We buy houses in any condition - from pristine to severely distressed. Whether your home has foundation issues, needs a new roof, has outdated systems, or requires cosmetic updates, we'll make you an offer. You don't need to fix anything, clean anything, or even remove your belongings. We handle everything after closing.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="w-14 h-14 flex items-center justify-center bg-black rounded-xl mb-6">
                <i className="ri-money-dollar-circle-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">No Hidden Fees or Costs</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Traditional sales come with realtor commissions (typically 6% of sale price), closing costs, title fees, inspection fees, and potential repair costs after inspection. These can easily total 10-15% of your home's value. When you sell to us, there are zero fees. No commissions, no closing costs, no surprise deductions. The offer we make is the amount you receive at closing - simple, transparent, and straightforward.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="w-14 h-14 flex items-center justify-center bg-black rounded-xl mb-6">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Avoid Foreclosure</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Facing foreclosure is stressful and can severely damage your credit for years. If you're behind on mortgage payments, we can help you avoid foreclosure by purchasing your home quickly, often with enough time to prevent the foreclosure from appearing on your credit report. We work directly with your lender to ensure a smooth transaction, helping you move forward with your financial future intact and your dignity preserved.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="w-14 h-14 flex items-center justify-center bg-black rounded-xl mb-6">
                <i className="ri-user-heart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Compassionate Service</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                We understand that selling your home often comes during challenging life transitions - divorce, job loss, medical issues, or the passing of a loved one. Our team treats every homeowner with respect, empathy, and professionalism. We're not just buying a house; we're helping people solve real problems and move forward with their lives. Your situation is unique, and we tailor our approach to meet your specific needs.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="w-14 h-14 flex items-center justify-center bg-black rounded-xl mb-6">
                <i className="ri-home-smile-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-black mb-4">Local El Paso Expertise</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                As local real estate investors, we have deep knowledge of the El Paso market, from the historic neighborhoods near downtown to the newer developments in the far east. We understand property values across all El Paso neighborhoods and can make informed, fair offers quickly. Our local presence means we can view your property promptly and close faster than out-of-state investors or national home-buying companies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section id="blog" className="bg-[#FAF8F5] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-16">
            <div>
              <p className="text-sm uppercase tracking-widest text-gray-500 mb-2">RESOURCES</p>
              <h2 className="text-5xl font-bold text-black">Latest Articles &amp; Tips</h2>
            </div>
            <Link to="/get-cash" className="text-base font-semibold text-black underline hover:text-gray-600 transition-colors whitespace-nowrap cursor-pointer">
              View All Posts
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <article className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <Link to="/get-cash/sell-house-fast-el-paso">
                <div className="h-56 overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=modern%20home%20selling%20process%20with%20documents%20and%20keys%20on%20desk%2C%20real%20estate%20transaction%20paperwork%2C%20professional%20business%20setting%20with%20warm%20lighting%2C%20clean%20organized%20workspace%20photography&width=600&height=400&seq=blog-001&orientation=landscape" 
                    alt="Blog post" 
                    className="w-full h-full object-cover grayscale-[20%] hover:grayscale-0 transition-all duration-300"
                  />
                </div>
                <div className="p-8">
                  <span className="inline-block px-3 py-1 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full mb-4">SELLING TIPS</span>
                  <h3 className="text-xl font-semibold text-black mb-3 line-clamp-2">How to Sell Your House Fast in El Paso: A Complete Guide</h3>
                  <p className="text-base text-gray-600 mb-4 line-clamp-3">
                    Discover the fastest methods to sell your El Paso home, including cash buyers, traditional listings, and everything in between. Learn which option is right for your situation.
                  </p>
                  <span className="text-sm font-semibold text-black underline inline-flex items-center gap-1 hover:gap-2 transition-all cursor-pointer">
                    Read More <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </Link>
            </article>

            <article className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <Link to="/get-cash/avoiding-foreclosure-options">
                <div className="h-56 overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=foreclosure%20notice%20document%20with%20house%20keys%20and%20calculator%2C%20financial%20stress%20concept%2C%20professional%20documentation%20photography%20with%20neutral%20background%2C%20realistic%20business%20imagery&width=600&height=400&seq=blog-002&orientation=landscape" 
                    alt="Blog post" 
                    className="w-full h-full object-cover grayscale-[20%] hover:grayscale-0 transition-all duration-300"
                  />
                </div>
                <div className="p-8">
                  <span className="inline-block px-3 py-1 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full mb-4">FORECLOSURE</span>
                  <h3 className="text-xl font-semibold text-black mb-3 line-clamp-2">Avoiding Foreclosure: Your Options when Behind on Payments</h3>
                  <p className="text-base text-gray-600 mb-4 line-clamp-3">
                    Facing foreclosure doesn't mean you're out of options. Learn about alternatives that can help you avoid foreclosure and protect your credit score in El Paso.
                  </p>
                  <span className="text-sm font-semibold text-black underline inline-flex items-center gap-1 hover:gap-2 transition-all cursor-pointer">
                    Read More <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </Link>
            </article>

            <article className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer">
              <Link to="/get-cash/inherit-house-texas">
                <div className="h-56 overflow-hidden">
                  <img 
                    src="https://readdy.ai/api/search-image?query=inherited%20house%20with%20vintage%20furniture%20and%20family%20photos%2C%20estate%20property%20interior%2C%20nostalgic%20home%20setting%20with%20warm%20natural%20light%2C%20emotional%20real%20estate%20photography&width=600&height=400&seq=blog-003&orientation=landscape" 
                    alt="Blog post" 
                    className="w-full h-full object-cover grayscale-[20%] hover:grayscale-0 transition-all duration-300"
                  />
                </div>
                <div className="p-8">
                  <span className="inline-block px-3 py-1 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full mb-4">INHERITANCE</span>
                  <h3 className="text-xl font-semibold text-black mb-3 line-clamp-2">What to Do when You Inherit a House in Texas</h3>
                  <p className="text-base text-gray-600 mb-4 line-clamp-3">
                    Inheriting property comes with decisions and responsibilities. Explore your options for inherited homes in El Paso, from selling quickly to long-term rental strategies.
                  </p>
                  <span className="text-sm font-semibold text-black underline inline-flex items-center gap-1 hover:gap-2 transition-all cursor-pointer">
                    Read More <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </Link>
            </article>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="bg-white py-28">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-5xl font-bold text-black text-center mb-20">Frequently Asked Questions</h2>

          <div className="space-y-0">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-gray-200">
                <button
                  onClick={() => setExpandedFaq(index)}
                  className="w-full py-8 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <span className="text-xl font-semibold text-black pr-8">{faq.question}</span>
                  <i className={`ri-${expandedFaq === index ? 'subtract' : 'add'}-line text-2xl text-black flex-shrink-0 transition-transform duration-300`}></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    expandedFaq === index ? 'max-h-96 pb-8' : 'max-h-0'
                  }`}
                >
                  <p className="text-lg text-gray-600 leading-relaxed pl-6">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="bg-[#2C2C2C] py-32">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold text-white mb-6">Ready to Sell Your House?</h2>
          <p className="text-2xl text-white/80 mb-12">Get your fair cash offer today - no obligations</p>
          
          <a href="tel:9157457883" className="inline-block text-6xl font-bold text-white mb-10 hover:text-[#D4AF37] transition-colors cursor-pointer">
            915-745-7883
          </a>

          <div>
            <button onClick={scrollToForm} className="px-14 py-5 bg-white text-black text-xl font-semibold rounded-full hover:scale-105 hover:shadow-2xl transition-all whitespace-nowrap cursor-pointer">
              Request Your Cash Offer
            </button>
          </div>

          <div className="flex items-center justify-center gap-12 mt-10">
            <div className="text-center">
              <i className="ri-shield-check-line text-2xl text-white/60 mb-2"></i>
              <p className="text-base text-white/60">No Obligation</p>
            </div>
            <div className="text-center">
              <i className="ri-timer-flash-line text-2xl text-white/60 mb-2"></i>
              <p className="text-base text-white/60">Fast Process</p>
            </div>
            <div className="text-center">
              <i className="ri-hand-coin-line text-2xl text-white/60 mb-2"></i>
              <p className="text-base text-white/60">Fair Offers</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
